"use client";

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";

import { useRouter } from "next/navigation";

import { v4 as uuidv4 } from "uuid";

import {
  type BrandResult,
  type ChatMessage,
  type SearchTool,
  parseAssistantContent,
} from "@/lib/chat-message-parser";
import {
  type StreamHandlerContext,
  handleEndEvent,
  handleErrorEvent,
  handleMessageEvent,
  handleMetadataEvent,
  handleSearchEvent,
} from "@/lib/chat-stream-handlers";
import { auth } from "@/lib/firebase-client";
import { searchMetadataStore } from "@/lib/search-metadata-store";
import {
  getShopAgentChatHistory,
  isEndEvent,
  isErrorEvent,
  isMessageEvent,
  isMetadataEvent,
  isSearchEvent,
  streamCosimoChat,
} from "@/lib/shop-agent-client";
import { type CategoryToken } from "@/utils/genderMapping";

// Re-export types from chat-message-parser for consumers
export type { BrandResult, ChatMessage, SearchTool };

// ============================================================================
// State & Reducer
// ============================================================================

interface ChatState {
  activeChatId: string | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  navigatedToResults: boolean;
  sourceResultsPath: string | null;
}

type ChatAction =
  | { type: "SET_ACTIVE_CHAT_ID"; chatId: string }
  | { type: "SET_MESSAGES"; messages: ChatMessage[] }
  | { type: "ADD_MESSAGE"; message: ChatMessage }
  | { type: "UPDATE_LAST_MESSAGE"; updates: Partial<ChatMessage> }
  | { type: "SET_STREAMING"; isStreaming: boolean }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "SET_NAVIGATED_TO_RESULTS"; navigated: boolean; resultsPath?: string }
  | { type: "RESET_ON_CHAT_CHANGE" }
  | {
      type: "RESTORE_SESSION";
      activeChatId: string;
      navigatedToResults: boolean;
      sourceResultsPath: string | null;
    }
  | { type: "CLEAR_CHAT" };

const initialState: ChatState = {
  activeChatId: null,
  messages: [],
  isStreaming: false,
  error: null,
  navigatedToResults: false,
  sourceResultsPath: null,
};

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "SET_ACTIVE_CHAT_ID":
      return { ...state, activeChatId: action.chatId };

    case "SET_MESSAGES":
      return { ...state, messages: action.messages };

    case "ADD_MESSAGE":
      return { ...state, messages: [...state.messages, action.message] };

    case "UPDATE_LAST_MESSAGE": {
      if (state.messages.length === 0) return state;
      const updated = [...state.messages];
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        ...action.updates,
      };
      return { ...state, messages: updated };
    }

    case "SET_STREAMING":
      return { ...state, isStreaming: action.isStreaming };

    case "SET_ERROR":
      return { ...state, error: action.error };

    case "SET_NAVIGATED_TO_RESULTS":
      return {
        ...state,
        navigatedToResults: action.navigated,
        sourceResultsPath: action.resultsPath ?? null,
      };

    case "RESET_ON_CHAT_CHANGE":
      // Only reset error, not isStreaming
      // Streaming state is now managed by loadChatHistory (which checks cached messages
      // for streaming status) and sendMessage. Resetting isStreaming here would overwrite
      // the correct streaming state restored by loadChatHistory when switching to a chat
      // with an active stream.
      return { ...state, error: null };

    case "RESTORE_SESSION":
      return {
        ...state,
        activeChatId: action.activeChatId,
        navigatedToResults: action.navigatedToResults,
        sourceResultsPath: action.sourceResultsPath,
      };

    case "CLEAR_CHAT":
      return { ...initialState };

    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

interface ChatContextType {
  // State
  activeChatId: string | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  navigatedToResults: boolean;
  sourceResultsPath: string | null;

  // Actions
  setActiveChatId: (chatId: string) => void;
  loadChatHistory: (chatId: string) => Promise<void>;
  sendMessage: (
    message: string,
    autoNavigate?: boolean,
    categoryOverride?: CategoryToken,
    threadIdOverride?: string,
    agentMessage?: string
  ) => Promise<void>;
  setNavigatedToResults: (navigated: boolean, resultsPath?: string) => void;
  clearChat: () => void;
  startNewChat: () => string;
  addMessage: (message: ChatMessage, chatId?: string) => void;
  updateLastMessage: (updates: Partial<ChatMessage>) => void;
  /** Track which search the user has selected within a conversation */
  setSelectedSearchId: (threadId: string, searchId: string) => void;
  getSelectedSearchId: (threadId: string) => string | null;
  setChatCategory: (chatId: string, category?: CategoryToken) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { activeChatId, messages, isStreaming, error, navigatedToResults, sourceResultsPath } =
    state;

  // Refs for mutable state that shouldn't trigger re-renders
  const hasLoadedRef = useRef<Set<string>>(new Set());
  const chatCategoriesRef = useRef<Map<string, CategoryToken>>(new Map());
  const shouldAutoNavigateRef = useRef(false);
  // Cache messages per chat to restore when switching between previously loaded chats
  const messagesCacheRef = useRef<Map<string, ChatMessage[]>>(new Map());
  // Track selected search ID per thread (threadId -> searchId)
  // This is set when user clicks a search chip in the chat to view results
  const selectedSearchIdsRef = useRef<Map<string, string>>(new Map());

  // Keep refs in sync for use in async callbacks without stale closures
  const messagesRef = useRef<ChatMessage[]>([]);
  const activeChatIdRef = useRef<string | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  // Reset streaming state when active chat changes
  useEffect(() => {
    dispatch({ type: "RESET_ON_CHAT_CHANGE" });
  }, [activeChatId]);

  // Load from sessionStorage on mount
  useEffect(() => {
    let savedState: string | null = null;
    try {
      savedState = sessionStorage.getItem("chatContext");
    } catch {
      return;
    }
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.activeChatId) {
          dispatch({
            type: "RESTORE_SESSION",
            activeChatId: parsed.activeChatId,
            navigatedToResults: parsed.navigatedToResults || false,
            sourceResultsPath: parsed.sourceResultsPath || null,
          });
        }
      } catch (err) {
        console.error("Failed to restore chat context:", err);
      }
    }
  }, []);

  // Save to sessionStorage when state changes
  useEffect(() => {
    if (activeChatId) {
      try {
        sessionStorage.setItem(
          "chatContext",
          JSON.stringify({
            activeChatId,
            navigatedToResults,
            sourceResultsPath,
          })
        );
      } catch {
        /* Keep public browsing available when persistence is denied. */
      }
    }
  }, [activeChatId, navigatedToResults, sourceResultsPath]);

  const loadChatHistory = useCallback(async (chatId: string, forceReload = false) => {
    console.debug("[ChatContext] loadChatHistory start", {
      chatId,
      forceReload,
      activeChatId: activeChatIdRef.current,
    });

    // Skip if already loaded unless forceReload is true
    if (!forceReload && hasLoadedRef.current.has(chatId)) {
      const cachedMessages = messagesCacheRef.current.get(chatId);
      if (cachedMessages) {
        console.debug("[ChatContext] Chat already loaded, restoring from cache:", chatId);

        // Check if cached messages have a streaming message
        // This prevents users from sending additional messages while streaming
        const lastCachedMessage = cachedMessages[cachedMessages.length - 1];
        const cachedHasStreaming =
          lastCachedMessage?.role === "assistant" && lastCachedMessage?.isStreaming === true;

        // Only update displayed state if still on the target chat
        if (activeChatIdRef.current === chatId) {
          messagesRef.current = cachedMessages;
          dispatch({ type: "SET_MESSAGES", messages: cachedMessages });
          // Restore streaming state based on cached messages
          // Must dispatch for both cases since RESET_ON_CHAT_CHANGE no longer resets streaming
          dispatch({ type: "SET_STREAMING", isStreaming: cachedHasStreaming });
        }
        return;
      }
      // Cache missing but hasLoadedRef says loaded - force a reload to avoid stale messages
      // This can happen if sendMessage marks chat as loaded but an error occurs before streaming updates cache
      console.debug("[ChatContext] Cache missing for loaded chat, forcing reload:", chatId);
      hasLoadedRef.current.delete(chatId);
    }

    // CRITICAL: Check the TARGET chat's cached messages for streaming status
    // NOT messagesRef.current, which may contain a different chat's messages during switching
    // (messagesRef is synced via useEffect which hasn't run yet after activeChatIdRef update)
    const targetChatMessages = messagesCacheRef.current.get(chatId) || [];
    const lastMessage = targetChatMessages[targetChatMessages.length - 1];
    const hasStreamingMessage =
      lastMessage?.role === "assistant" && lastMessage?.isStreaming === true;

    // Don't reload history if we have a streaming message for THIS chat
    // But DO restore the cached messages to display (fixes case where hasLoadedRef was cleared
    // while a stream was still writing to the cache)
    if (hasStreamingMessage) {
      console.debug(
        "[ChatContext] Streaming message detected for target chat, restoring cache without API fetch"
      );
      if (activeChatIdRef.current === chatId && targetChatMessages.length > 0) {
        messagesRef.current = targetChatMessages;
        dispatch({ type: "SET_MESSAGES", messages: targetChatMessages });
        dispatch({ type: "SET_STREAMING", isStreaming: true });
      }
      return;
    }

    // Wait for Firebase auth to be ready
    const maxWaitTime = 5000;
    const startTime = Date.now();
    while (!auth?.currentUser && Date.now() - startTime < maxWaitTime) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (!auth?.currentUser) {
      console.debug("[ChatContext] Auth not ready, aborting history load");
      // Reset streaming state to prevent stale state from blocking message sending
      if (activeChatIdRef.current === chatId) {
        dispatch({ type: "SET_STREAMING", isStreaming: false });
      }
      return;
    }

    try {
      const chatHistory = await getShopAgentChatHistory(chatId);
      console.debug("[ChatContext] Loaded history", {
        chatId,
        messageCount: chatHistory.messages.length,
      });

      const loadedMessages: ChatMessage[] = chatHistory.messages.map((msg) => {
        if (msg.role === "assistant" && typeof msg.content === "string") {
          const parsed = parseAssistantContent(msg.content);

          if (parsed) {
            return {
              role: msg.role,
              content: parsed.content,
              timestamp: msg.timestamp,
              isStreaming: false,
              searchTools: parsed.searchTools.length > 0 ? parsed.searchTools : undefined,
              brandResults: parsed.brandResults.length > 0 ? parsed.brandResults : undefined,
            };
          }

          console.warn("[ChatContext] Could not parse assistant message, using raw content");
          return {
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp,
            isStreaming: false,
          };
        }

        return {
          role: msg.role,
          content: typeof msg.content === "string" ? msg.content : JSON.stringify(msg.content),
          timestamp: msg.timestamp,
          isStreaming: false,
        };
      });

      // Check if sendMessage added messages while we were waiting for the API
      // If so, don't overwrite the cache - the user's new messages take precedence
      const existingMessages = messagesCacheRef.current.get(chatId);
      const existingLastMessage = existingMessages?.[existingMessages.length - 1];
      const existingHasStreaming =
        existingLastMessage?.role === "assistant" && existingLastMessage?.isStreaming === true;

      // Don't overwrite if:
      // - Cache has more messages than API returned (user sent new messages)
      // - Cache has a streaming message (active stream in progress)
      const shouldUpdateCache =
        !existingMessages ||
        existingMessages.length === 0 ||
        (!existingHasStreaming && existingMessages.length <= loadedMessages.length);

      if (shouldUpdateCache) {
        messagesCacheRef.current.set(chatId, loadedMessages);
      }
      hasLoadedRef.current.add(chatId);

      // Only update displayed state if still on the target chat and we updated the cache
      if (activeChatIdRef.current === chatId && shouldUpdateCache) {
        messagesRef.current = loadedMessages;
        dispatch({ type: "SET_MESSAGES", messages: loadedMessages });
        // Always reset streaming state - if there was an active stream, we would have
        // returned early when checking hasStreamingMessage above
        dispatch({ type: "SET_STREAMING", isStreaming: false });
      }
    } catch (err) {
      console.debug("[ChatContext] Failed to load chat history:", err);
      // Only cache empty messages if no messages already exist for this chat
      // This prevents overwriting messages added by sendMessage while we were waiting for the API
      const existingMessages = messagesCacheRef.current.get(chatId);
      if (!existingMessages || existingMessages.length === 0) {
        messagesCacheRef.current.set(chatId, []);
      }
      hasLoadedRef.current.add(chatId);

      // Only update displayed state if still on the target chat AND no messages exist
      // This prevents clearing messages that sendMessage added during the API wait
      if (activeChatIdRef.current === chatId) {
        const currentCacheForChat = messagesCacheRef.current.get(chatId) || [];
        if (currentCacheForChat.length === 0) {
          messagesRef.current = [];
          dispatch({ type: "SET_MESSAGES", messages: [] });
        }
        dispatch({ type: "SET_STREAMING", isStreaming: false });
      }
    }
  }, []);

  // Helper to update cache for a specific chat
  const updateCacheForChat = useCallback((chatId: string, messages: ChatMessage[]) => {
    messagesCacheRef.current.set(chatId, messages);
  }, []);

  // Factory to create a setMessages wrapper bound to a specific chat ID
  // This prevents streaming updates from being cached under the wrong chat if user switches chats
  const createSetMessagesForChat = useCallback(
    (targetChatId: string) => (updater: React.SetStateAction<ChatMessage[]>) => {
      // CRITICAL: Read from the TARGET chat's cache, not messagesRef.current
      // When user switches chats during streaming, messagesRef.current contains the NEW chat's messages.
      // We must use the cache for the streaming chat to compute updates correctly.
      const currentMessagesForChat = messagesCacheRef.current.get(targetChatId) || [];
      let newMessages: ChatMessage[];
      if (typeof updater === "function") {
        // For function updaters, compute new state from the TARGET chat's cached messages
        newMessages = updater(currentMessagesForChat);
      } else {
        newMessages = updater;
      }
      // Update cache for the TARGET chat (captured at stream start)
      updateCacheForChat(targetChatId, newMessages);
      // Only update the displayed messages if we're still on the same chat
      if (activeChatIdRef.current === targetChatId) {
        messagesRef.current = newMessages;
        dispatch({ type: "SET_MESSAGES", messages: newMessages });
      }
    },
    [updateCacheForChat]
  );

  // Factory to create a setError wrapper bound to a specific chat ID
  // This prevents error updates from appearing on the wrong chat if user switches chats
  const createSetErrorForChat = useCallback(
    (targetChatId: string) => (error: string | null) => {
      // Only update error state if still on the same chat
      if (activeChatIdRef.current === targetChatId) {
        dispatch({ type: "SET_ERROR", error });
      }
    },
    []
  );

  const sendMessage = useCallback(
    async (
      message: string,
      autoNavigate: boolean = true,
      categoryOverride?: CategoryToken,
      threadIdOverride?: string,
      agentMessage?: string
    ) => {
      // Use threadIdOverride if provided, otherwise fall back to activeChatId.
      // This allows callers to specify the thread ID directly without waiting for
      // async state updates from setActiveChatId().
      const effectiveThreadId = threadIdOverride || activeChatId;

      // When threadIdOverride is provided, we're explicitly starting a new chat
      // and should bypass the isStreaming check. This prevents a new thread from
      // being blocked by stale isStreaming state left over from a previous thread
      // (e.g., when navigating between results pages while streaming is in progress).
      const shouldCheckStreaming = !threadIdOverride;

      if (!effectiveThreadId || !message.trim() || (shouldCheckStreaming && isStreaming)) {
        return;
      }

      // Capture the chatId at the start - this is the chat we're sending to
      const streamingChatId = effectiveThreadId;

      // agentMessage is what we send to the AI agent for processing.
      // It defaults to `message` but can be different (e.g., including gender context
      // for search while showing the user's original input in the chat).
      const messageForAgent = agentMessage || message;

      console.debug("[ChatContext] sendMessage", {
        streamingChatId,
        autoNavigate,
        hasCategoryOverride: categoryOverride !== undefined,
        hasAgentMessage: !!agentMessage,
      });

      const userTimestamp = new Date().toISOString();
      const userMessage: ChatMessage = {
        role: "user",
        content: message,
        timestamp: userTimestamp,
      };

      // CRITICAL: Read from the TARGET chat's cache to avoid mixing messages
      // If cache is empty, use empty array (not messagesRef.current which may have different chat's messages)
      const currentChatMessages = messagesCacheRef.current.get(streamingChatId) || [];
      const messagesWithUser = [...currentChatMessages, userMessage];

      // Update cache immediately for the streaming chat
      updateCacheForChat(streamingChatId, messagesWithUser);
      hasLoadedRef.current.add(streamingChatId);

      // Only update displayed state if still on the same chat
      // CRITICAL: Use SET_MESSAGES with the computed array, not ADD_MESSAGE
      // ADD_MESSAGE appends to state.messages which may contain a different chat's messages
      if (activeChatIdRef.current === streamingChatId) {
        messagesRef.current = messagesWithUser;
        dispatch({ type: "SET_MESSAGES", messages: messagesWithUser });
        dispatch({ type: "SET_STREAMING", isStreaming: true });
        dispatch({ type: "SET_ERROR", error: null });
      }

      // Set flag to auto-navigate to first search result (for ChatWindow conversations)
      shouldAutoNavigateRef.current = autoNavigate;

      const assistantTimestamp = new Date().toISOString();
      const assistantPlaceholder: ChatMessage = {
        role: "assistant",
        content: "",
        isStreaming: true,
        timestamp: assistantTimestamp,
        searchTools: [],
      };

      // Compute messages with assistant placeholder added and update cache immediately
      const messagesWithPlaceholder = [...messagesWithUser, assistantPlaceholder];
      updateCacheForChat(streamingChatId, messagesWithPlaceholder);

      // Capture the assistant message index for concurrent stream safety.
      // This ensures handlers update the correct message even if another stream
      // adds messages to the array while this stream is still processing.
      const assistantMessageIndex = messagesWithPlaceholder.length - 1;

      // Only update displayed state if still on the same chat
      // CRITICAL: Use SET_MESSAGES with the computed array, not ADD_MESSAGE
      if (activeChatIdRef.current === streamingChatId) {
        messagesRef.current = messagesWithPlaceholder;
        dispatch({ type: "SET_MESSAGES", messages: messagesWithPlaceholder });
      }

      // Handle category override
      if (categoryOverride !== undefined) {
        if (categoryOverride) {
          chatCategoriesRef.current.set(streamingChatId, categoryOverride);
        } else {
          chatCategoriesRef.current.delete(streamingChatId);
        }
      }
      console.debug("[ChatContext] Category context", {
        streamingChatId,
        category: chatCategoriesRef.current.get(streamingChatId),
      });

      const categoryContext = chatCategoriesRef.current.get(streamingChatId);

      // Get selected search ID for this thread (set when user clicks a search chip)
      // Only include for follow-up messages to avoid attaching context to the first message
      const isFollowUpMessage = currentChatMessages.length > 0;
      const selectedSearchId = isFollowUpMessage
        ? selectedSearchIdsRef.current.get(streamingChatId) || null
        : null;

      // Create chat-scoped message handler to ensure updates go to the correct chat
      const setMessagesForThisChat = createSetMessagesForChat(streamingChatId);
      // Create chat-scoped error handler to ensure errors appear on the correct chat
      const setErrorForThisChat = createSetErrorForChat(streamingChatId);

      // Create handler context with all dependencies
      const handlerContext: StreamHandlerContext = {
        searchTools: [],
        brandResults: [],
        assistantTimestamp,
        shouldAutoNavigate: shouldAutoNavigateRef,
        router,
        searchMetadataStore,
        setMessages: setMessagesForThisChat,
        setError: setErrorForThisChat,
        threadId: streamingChatId,
        selectedSearchId,
        assistantMessageIndex,
      };

      try {
        for await (const event of streamCosimoChat({
          message: messageForAgent,
          threadId: streamingChatId,
          category: categoryContext,
          selectedSearchId,
        })) {
          let result: "continue" | "break" = "continue";

          if (isMetadataEvent(event.data)) {
            result = handleMetadataEvent(event.data, handlerContext);
          } else if (isSearchEvent(event.data)) {
            result = handleSearchEvent(event.data, handlerContext);
          } else if (isMessageEvent(event.data)) {
            result = handleMessageEvent(event.data, handlerContext);
          } else if (isErrorEvent(event.data)) {
            result = handleErrorEvent(event.data, handlerContext);
          } else if (isEndEvent(event.data)) {
            result = handleEndEvent();
          }

          if (result === "break") {
            break;
          }
        }

        // Only update streaming state if still on the same chat
        if (activeChatIdRef.current === streamingChatId) {
          dispatch({ type: "SET_STREAMING", isStreaming: false });
        }
      } catch (err) {
        console.error("Stream error:", err);
        // Only update error state if still on the same chat
        if (activeChatIdRef.current === streamingChatId) {
          dispatch({
            type: "SET_ERROR",
            error: err instanceof Error ? err.message : "Failed to get response",
          });
        }
        // Use chat-scoped handler to update the error message in the correct chat's cache
        // This ensures the error is written to the streaming chat even if user switched away
        // Use the captured assistantMessageIndex for concurrent stream safety
        setMessagesForThisChat((prev) => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          const targetMessage = updated[assistantMessageIndex];
          if (!targetMessage) return prev;
          updated[assistantMessageIndex] = {
            ...targetMessage,
            content: "Sorry, I encountered an error processing your request.",
            isStreaming: false,
            timestamp: assistantTimestamp,
          };
          return updated;
        });
        // Only update streaming state if still on the same chat
        if (activeChatIdRef.current === streamingChatId) {
          dispatch({ type: "SET_STREAMING", isStreaming: false });
        }
      }
    },
    [
      activeChatId,
      isStreaming,
      router,
      createSetMessagesForChat,
      createSetErrorForChat,
      updateCacheForChat,
    ]
  );

  const setActiveChatIdAction = useCallback((chatId: string) => {
    console.debug("[ChatContext] setActiveChatId", { chatId });
    // CRITICAL: Update ref synchronously before dispatch
    // This ensures loadChatHistory sees the correct chatId when called
    // immediately after setActiveChatId (before the useEffect that syncs the ref runs)
    activeChatIdRef.current = chatId;
    dispatch({ type: "SET_ACTIVE_CHAT_ID", chatId });
  }, []);

  const setNavigatedToResults = useCallback((navigated: boolean, resultsPath?: string) => {
    dispatch({ type: "SET_NAVIGATED_TO_RESULTS", navigated, resultsPath });
  }, []);

  const clearChat = useCallback(() => {
    dispatch({ type: "CLEAR_CHAT" });
    sessionStorage.removeItem("chatContext");
    hasLoadedRef.current.clear();
    chatCategoriesRef.current.clear();
    messagesCacheRef.current.clear();
    selectedSearchIdsRef.current.clear();
  }, []);

  const startNewChat = useCallback(() => {
    // Clear any existing chat state
    dispatch({ type: "CLEAR_CHAT" });
    sessionStorage.removeItem("chatContext");
    chatCategoriesRef.current.clear();

    // Create new chat ID
    const newChatId = uuidv4();

    // Mark as loaded to skip history fetch (it's a new chat, no history exists)
    hasLoadedRef.current.clear();
    hasLoadedRef.current.add(newChatId);

    // Initialize empty cache for the new chat
    messagesCacheRef.current.clear();
    selectedSearchIdsRef.current.clear();
    messagesCacheRef.current.set(newChatId, []);

    // CRITICAL: Update ref synchronously before dispatch
    // This ensures sendMessage sees the correct chatId when called immediately
    // after startNewChat (before the useEffect that syncs the ref runs)
    activeChatIdRef.current = newChatId;

    // Set as active chat
    dispatch({ type: "SET_ACTIVE_CHAT_ID", chatId: newChatId });

    console.debug("[ChatContext] startNewChat", { newChatId });
    return newChatId;
  }, []);

  const setChatCategory = useCallback((chatId: string, category?: CategoryToken) => {
    if (!chatId) {
      return;
    }

    if (category) {
      chatCategoriesRef.current.set(chatId, category);
    } else {
      chatCategoriesRef.current.delete(chatId);
    }
  }, []);

  const setSelectedSearchId = useCallback((threadId: string, searchId: string) => {
    if (!threadId || !searchId) {
      return;
    }
    selectedSearchIdsRef.current.set(threadId, searchId);
    console.debug("[ChatContext] setSelectedSearchId", { threadId, searchId });
  }, []);

  const getSelectedSearchId = useCallback((threadId: string) => {
    if (!threadId) {
      return null;
    }
    return selectedSearchIdsRef.current.get(threadId) || null;
  }, []);
  const addMessage = useCallback(
    (message: ChatMessage, chatId?: string) => {
      // CRITICAL: Capture targetChatId first and always read from that chat's cache
      // During chat transitions, activeChatIdRef updates synchronously but messagesRef
      // only syncs via useEffect after render. Reading from messagesRef could get
      // the wrong chat's messages and write them to the new chat's cache.
      const currentActiveChatId = activeChatIdRef.current;
      const targetChatId = chatId || currentActiveChatId;

      if (!targetChatId) {
        return;
      }

      // Always read from the TARGET chat's cache, not messagesRef.current
      // This prevents reading stale messages during chat transitions
      const baseMessages = messagesCacheRef.current.get(targetChatId) || [];
      const updatedMessages = [...baseMessages, message];

      // Update cache for the target chat
      hasLoadedRef.current.add(targetChatId);
      updateCacheForChat(targetChatId, updatedMessages);

      // Only update displayed messages and ref if targeting the current chat
      // CRITICAL: Use SET_MESSAGES with the computed array, not ADD_MESSAGE
      // ADD_MESSAGE appends to state.messages which may be out of sync with messagesRef
      if (targetChatId === currentActiveChatId) {
        messagesRef.current = updatedMessages;
        dispatch({ type: "SET_MESSAGES", messages: updatedMessages });
      }
    },
    [updateCacheForChat]
  );

  const updateLastMessage = useCallback(
    (updates: Partial<ChatMessage>) => {
      // CRITICAL: Capture chatId first and read from that chat's cache
      // This prevents corruption during chat transitions where activeChatIdRef
      // might point to a different chat than messagesRef.current
      const chatId = activeChatIdRef.current;
      if (!chatId) {
        return;
      }

      // Read from the cache for the target chat, not messagesRef.current
      // During transitions, messagesRef may still have old chat's messages
      const currentMessages = messagesCacheRef.current.get(chatId) || [];
      if (currentMessages.length === 0) {
        return;
      }

      // Compute the updated messages
      const updatedMessages = [...currentMessages];
      updatedMessages[updatedMessages.length - 1] = {
        ...updatedMessages[updatedMessages.length - 1],
        ...updates,
      };

      // Update cache for the same chat we read from
      updateCacheForChat(chatId, updatedMessages);

      // Only update displayed state if still viewing this chat
      // Use SET_MESSAGES with computed array for consistency
      if (activeChatIdRef.current === chatId) {
        messagesRef.current = updatedMessages;
        dispatch({ type: "SET_MESSAGES", messages: updatedMessages });
      }
    },
    [updateCacheForChat]
  );

  const value: ChatContextType = {
    activeChatId,
    messages,
    isStreaming,
    error,
    navigatedToResults,
    sourceResultsPath,
    setActiveChatId: setActiveChatIdAction,
    loadChatHistory,
    sendMessage,
    setNavigatedToResults,
    clearChat,
    startNewChat,
    addMessage,
    updateLastMessage,
    setChatCategory,
    setSelectedSearchId,
    getSelectedSearchId,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
